import { useMemo } from 'react';
import dynamic from 'next/dynamic';

import clsx from 'clsx';
import {
    Area,
    Bar,
    CartesianGrid,
    ComposedChart,
    Rectangle,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';

import useFormatDateTime from '@/hooks/format/useFormatDateTime';
import useFormatNumber from '@/hooks/format/useFormatNumber';
import useTranslation from '@/utils/hooks/translation/useTranslation';

// Hooks
import useDarkMode from '@/hooks/utils/useDarkMode';

import PropTypes from 'prop-types';

// Lib
import { darkModeColorsChart } from '@/lib/analytics/colors';

import { SizeMe } from '@/components/base';
// Components
import TooltipCustom from './components/TooltipCustom';

import ChartWrapper from './ChartWrapper';

const DynamicTableChart = dynamic(() => import('./components/TableChart'));

const CustomHourAxisTick = ({ x, y, payload, format, isExport }) => {
    return (
        <g transform={`translate(${x},${y})`}>
            <text
                x={0}
                y={0}
                dy={10}
                textAnchor="end"
                transform="rotate(-45)"
                className={clsx('fill-current font-GSemiBold text-xxs text-icoGray-500', {
                    'dark:text-icoDarkMode-wolf': !isExport
                })}>
                {format ? format(payload.value) : payload.value}
            </text>
        </g>
    );
};

CustomHourAxisTick.propTypes = {
    x: PropTypes.number,
    y: PropTypes.number,
    payload: PropTypes.object
};

const HalfBarLineChartComponent = ({
    data,
    activeTable,
    columnNames,
    userNumbersFormat,
    isExport,
    hiddenKeys,
    height = 230
}) => {
    const { t } = useTranslation('analytics');
    const { isDarkMode } = useDarkMode();
    const isDateChart = useMemo(() => Object.keys(data.name)[0] === 'date', [data]);
    const theme = isDarkMode() && !isExport ? 'dark' : 'light';
    const { formatNumber } = useFormatNumber(userNumbersFormat?.number);
    const { formatDateTimeNoTz } = useFormatDateTime(userNumbersFormat?.date?.timezone);

    const lineNameKey = useMemo(
        () => Object.keys(data.name)[Object.entries(data.name).length - 1],
        [data]
    );

    const ignoredData = useMemo(() => ['age', 'date', 'total', 'posts'], []);

    const finalData = useMemo(() => {
        let finalData = [];
        switch (data.id) {
            case 'gained_lost_followers_history':
            case 'gained_lost_fans_history':
                data.graphData.map((item) => {
                    finalData.push({
                        ...item,
                        lost: item.lost > 0 ? item.lost : 0 - item.lost,
                        total: item.lost > 0 ? item.gained - item.lost : item.gained + item.lost
                    });
                });

                return finalData;
            default:
                return data.graphData;
        }
    }, [data.graphData, data.id]);

    const finalColumnNames = useMemo(() => {
        if (data.id === 'gained_lost_fans_history' || data.id === 'gained_lost_followers_history') {
            let obj = {};

            if (finalData.length) {
                Object.entries(finalData[0])?.map((item) => {
                    obj[item[0]] = t(data.name[item[0]]);
                });
            }

            return obj;
        }

        return columnNames;
    }, [data.id, data.name, columnNames, finalData, t]);

    const YAxisDomain = useMemo(() => {
        if (data.id === 'gained_lost_fans_history' || data.id === 'gained_lost_followers_history') {
            let minDomain = 0;
            let maxDomain = 0;

            finalData.map((item) => {
                const itemMinValue = parseInt(item.lost);
                const itemMaxValue = item.gained > item.posts ? item.gained : item.posts;
                minDomain = itemMinValue < minDomain ? itemMinValue : minDomain;
                maxDomain = itemMaxValue > maxDomain ? itemMaxValue : maxDomain;
            });
            return [Number(minDomain), Number(maxDomain)];
        }

        return null;
    }, [finalData, data.id]);

    return (
        <>
            {activeTable ? (
                <DynamicTableChart
                    isExport={isExport}
                    graphData={finalData}
                    columnNames={finalColumnNames}
                    timeFormat={data.timeFormatter}
                    userNumbersFormat={userNumbersFormat}
                />
            ) : (
                <SizeMe>
                    {({ size }) => (
                        <div
                            className="relative w-full lg:h-full"
                            style={{ minHeight: isExport ? 180 : height }}>
                            <div className="absolute left-0 top-0 h-full w-full">
                                <ResponsiveContainer
                                    height={isExport ? 180 : height}
                                    width={isExport ? data.fixedSize : '100%'}
                                    minWidth={data.minWidth}>
                                    <ComposedChart
                                        data={finalData}
                                        stackOffset="sign"
                                        barSize={10}
                                        layout={data.orientation}
                                        margin={data.margin}>
                                        <defs>
                                            <linearGradient
                                                id={`gradient-${data.id}`}
                                                x1="0"
                                                y1="0"
                                                x2="0"
                                                y2="1">
                                                <stop
                                                    offset="5%"
                                                    stopColor={
                                                        darkModeColorsChart[theme][
                                                            data.color[lineNameKey]
                                                        ]
                                                    }
                                                    stopOpacity={0.3}
                                                />
                                                <stop
                                                    offset="95%"
                                                    stopColor={
                                                        darkModeColorsChart[theme][
                                                            data?.color[lineNameKey]
                                                        ]
                                                    }
                                                    stopOpacity={0}
                                                />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid
                                            vertical={data.orientation === 'vertical'}
                                            horizontal={data.orientation === 'horizontal'}
                                            stroke={darkModeColorsChart[theme].grid}
                                        />
                                        <XAxis
                                            dataKey={
                                                data.orientation === 'horizontal' &&
                                                Object.keys(data.name)[0]
                                            }
                                            tick={
                                                data.graphData.length > 8 && isDateChart ? (
                                                    <CustomHourAxisTick
                                                        isExport={isExport}
                                                        format={(item) =>
                                                            formatDateTimeNoTz(
                                                                item,
                                                                userNumbersFormat?.date?.format
                                                            )
                                                        }
                                                    />
                                                ) : (
                                                    {}
                                                )
                                            }
                                            tickFormatter={
                                                data.graphData.length <= 8 || !isDateChart
                                                    ? (item) =>
                                                          isDateChart
                                                              ? formatDateTimeNoTz(
                                                                    item,
                                                                    userNumbersFormat?.date?.format
                                                                )
                                                              : formatNumber(item, data.valueFormat)
                                                    : null
                                            }
                                            type={
                                                data.orientation === 'horizontal'
                                                    ? 'category'
                                                    : 'number'
                                            }
                                            tickLine={false}
                                            axisLine={false}
                                            minTickGap={isDateChart && -55}
                                            interval={
                                                // responsive xAxis labels
                                                isDateChart &&
                                                data.graphData.length * 20 > size.width
                                                    ? Math.round(
                                                          data.graphData.length / (size.width / 20)
                                                      )
                                                    : 0
                                            }
                                        />
                                        <YAxis
                                            yAxisId="left"
                                            dataKey={
                                                data.orientation === 'vertical' &&
                                                Object.keys(data.name)[0]
                                            }
                                            tickCount={5}
                                            domain={YAxisDomain}
                                            type={
                                                data.orientation === 'vertical'
                                                    ? 'category'
                                                    : 'number'
                                            }
                                            tickLine={false}
                                            axisLine={false}
                                            tickFormatter={(item) =>
                                                data.orientation !== 'vertical'
                                                    ? formatNumber(item, data.valueFormat)
                                                    : item
                                            }
                                            label={
                                                data.labelLeftAxis
                                                    ? {
                                                          value: t(data.labelLeftAxis),
                                                          angle: -90,
                                                          position: 'inside'
                                                      }
                                                    : null
                                            }
                                        />
                                        <YAxis
                                            orientation="right"
                                            yAxisId="right"
                                            domain={YAxisDomain}
                                            dataKey={lineNameKey}
                                            tickCount={5}
                                            type={
                                                data.orientation === 'vertical'
                                                    ? 'category'
                                                    : 'number'
                                            }
                                            tickLine={false}
                                            axisLine={false}
                                            tickFormatter={(item) =>
                                                data.orientation !== 'vertical'
                                                    ? item < 0
                                                        ? ''
                                                        : formatNumber(item, data.valueFormat)
                                                    : item
                                            }
                                            label={
                                                data.labelRightAxis
                                                    ? {
                                                          value: t(data.labelRightAxis),
                                                          angle: 90,
                                                          position: 'inside'
                                                      }
                                                    : null
                                            }
                                        />
                                        <ReferenceLine
                                            yAxisId="left"
                                            x={data.orientation === 'vertical' ? 0 : null}
                                            y={data.orientation === 'horizontal' ? 0 : null}
                                            stroke={darkModeColorsChart[theme].referenceLine}
                                        />
                                        <Tooltip
                                            cursor={false}
                                            animationDuration={0}
                                            isAnimationActive={false}
                                            content={
                                                <TooltipCustom
                                                    tooltipTranslation={data.tooltipTranslation}
                                                    valueFormat={{ signDisplay: 'never' }}
                                                    data={data}
                                                />
                                            }
                                        />
                                        {Object.entries(finalData[0]).map((item, index) => {
                                            if (
                                                !ignoredData.includes(item[0]) &&
                                                !hiddenKeys?.[item[0]]
                                            ) {
                                                return (
                                                    <Bar
                                                        isAnimationActive={!isExport}
                                                        yAxisId="left"
                                                        key={index}
                                                        name={t(data.name[item[0]])}
                                                        dataKey={item[0]}
                                                        fill={
                                                            darkModeColorsChart[theme][
                                                                data.color[item[0]]
                                                            ]
                                                        }
                                                        stackId="stack"
                                                        shape={(props) => {
                                                            props.radius =
                                                                data.orientation === 'vertical'
                                                                    ? [0, 2.5, 2.5, 0]
                                                                    : [2.5, 2.5, 0, 0];

                                                            return <Rectangle {...props} />;
                                                        }}
                                                    />
                                                );
                                            }
                                        })}
                                        {!hiddenKeys?.[lineNameKey] && (
                                            <Area
                                                yAxisId="right"
                                                isAnimationActive={!isExport}
                                                name={t(data.name[lineNameKey])}
                                                type="monotone"
                                                dataKey={lineNameKey}
                                                stroke={
                                                    darkModeColorsChart[theme][
                                                        data.color[lineNameKey]
                                                    ]
                                                }
                                                strokeWidth={2.5}
                                                fillOpacity={1}
                                                fill={`url(#gradient-${data.id})`}
                                                activeDot={{
                                                    stroke: darkModeColorsChart[theme].dot,
                                                    strokeWidth: 4.5,
                                                    r: 5
                                                }}
                                            />
                                        )}
                                    </ComposedChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    )}
                </SizeMe>
            )}
        </>
    );
};

HalfBarLineChartComponent.propTypes = {
    data: PropTypes.object,
    activeTable: PropTypes.bool,
    columnNames: PropTypes.object,
    userNumbersFormat: PropTypes.object,
    height: PropTypes.number,
    isExport: PropTypes.bool,
    hiddenKeys: PropTypes.object
};

const HalfBarLineChart = ({
    data,
    className = '',
    onReset = () => {
        /** */
    },
    isLoading = false,
    ...restProps
}) => (
    <ChartWrapper
        className={className}
        isLoading={isLoading}
        onReset={onReset}
        data={data}
        legendType="square"
        {...restProps}>
        <HalfBarLineChartComponent />
    </ChartWrapper>
);

HalfBarLineChart.propTypes = {
    data: PropTypes.object,
    className: PropTypes.string,
    onReset: PropTypes.func,
    isLoading: PropTypes.bool
};

export { HalfBarLineChart, HalfBarLineChartComponent };
